import {
  addDoc,
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { auth, db } from "../firebase";

const safeLocalStorage = {
  get(key) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Analytics should never block user actions.
    }
  },
};

export function getAnalyticsSessionId() {
  const existingSession = safeLocalStorage.get("qaCoderAnalyticsSession");

  if (existingSession) {
    return existingSession;
  }

  const sessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  safeLocalStorage.set("qaCoderAnalyticsSession", sessionId);
  return sessionId;
}

function getBaseEventData() {
  return {
    sessionId: getAnalyticsSessionId(),
    userId: auth.currentUser?.uid || null,
    userEmail: auth.currentUser?.email || null,
    userAgent: navigator.userAgent,
    createdAt: serverTimestamp(),
  };
}

async function writeAnalyticsEvent(collectionName, payload) {
  try {
    await addDoc(collection(db, collectionName), {
      ...getBaseEventData(),
      ...payload,
    });
  } catch (error) {
    console.warn(`Unable to write ${collectionName} analytics event`, error);
  }
}

export function trackPageView({ path, title, previousPath }) {
  return writeAnalyticsEvent("page_views", {
    path,
    title,
    previousPath: previousPath || null,
  });
}

export function trackSearchLog({ queryText, selectedLabel, selectedPath, category, resultCount }) {
  return writeAnalyticsEvent("search_logs", {
    queryText,
    selectedLabel,
    selectedPath,
    category,
    resultCount,
  });
}

export function trackCtaClick({ label, location, targetPath, sourcePath }) {
  return writeAnalyticsEvent("cta_clicks", {
    label,
    location,
    targetPath,
    sourcePath,
  });
}

export function trackCourseInterest({ courseTitle, action, sourcePath }) {
  return writeAnalyticsEvent("course_interest", {
    courseTitle,
    action,
    sourcePath,
  });
}

export function trackUserActivity({ action, sourcePath, metadata = {} }) {
  return writeAnalyticsEvent("user_activity", {
    action,
    sourcePath,
    metadata,
  });
}

export async function getCollectionCount(collectionName) {
  const snapshot = await getDocs(collection(db, collectionName));
  return snapshot.size;
}

export async function getCollectionCountSince(collectionName, date, field = "createdAt") {
  const analyticsQuery = query(collection(db, collectionName), where(field, ">=", date));
  const snapshot = await getDocs(analyticsQuery);
  return snapshot.size;
}

export async function getRecentDocuments(collectionName, maxItems = 250) {
  const analyticsQuery = query(
    collection(db, collectionName),
    orderBy("createdAt", "desc"),
    limit(maxItems)
  );
  const snapshot = await getDocs(analyticsQuery);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
}

export function countByField(items, field, fallback = "Unknown") {
  return items.reduce((accumulator, item) => {
    const key = item[field] || fallback;
    accumulator[key] = (accumulator[key] || 0) + 1;
    return accumulator;
  }, {});
}

export function getTopEntries(countMap, maxItems = 5) {
  return Object.entries(countMap)
    .map(([label, value]) => ({ label, value }))
    .sort((first, second) => second.value - first.value)
    .slice(0, maxItems);
}
